import { useEffect, useState } from "react";
import { loadMealsRemote } from "../lib/storage";
import { loadWeights } from "../lib/weight";
import { computeTDEE, type TDEEResult } from "../lib/tdee";
import type { Goals } from "../lib/goals";

interface Props {
  goals: Goals;
}

export default function TDEECard({ goals }: Props) {
  const [result, setResult] = useState<TDEEResult | null | "loading">("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meals, weights] = await Promise.all([loadMealsRemote(), loadWeights()]);
        const r = computeTDEE(meals, weights);
        if (!cancelled) setResult(r);
      } catch {
        if (!cancelled) setResult(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (result === "loading") {
    return (
      <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-sm text-slate-500">
        Computing TDEE…
      </section>
    );
  }

  if (!result) {
    return (
      <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-2">
        <div className="text-xs text-slate-400 uppercase tracking-wide">Computed TDEE</div>
        <div className="text-sm text-slate-500">
          Log your weight for 7+ days (and your meals daily) to get a personalized maintenance-calorie estimate.
        </div>
      </section>
    );
  }

  const diff = goals.calories - result.tdee_kcal;
  const lbPerWeek = (diff * 7) / 3500;
  const direction = diff < 0 ? "deficit" : diff > 0 ? "surplus" : "maintenance";

  return (
    <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
      <div className="flex justify-between items-baseline">
        <div className="text-xs text-slate-400 uppercase tracking-wide">Computed TDEE</div>
        <div className="text-xs text-slate-500">over {result.days} days</div>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-emerald-400 tabular-nums">{result.tdee_kcal}</span>
        <span className="text-sm text-slate-400">kcal / day maintenance</span>
      </div>
      <div className="text-xs text-slate-500 leading-relaxed">
        Based on your average intake of <span className="text-slate-200">{result.avg_intake} kcal/day</span> and weight change of <span className="text-slate-200">{result.weight_change_lbs > 0 ? "+" : ""}{result.weight_change_lbs} lb</span> over the period.
      </div>
      <div className="text-sm text-slate-300 pt-2 border-t border-slate-800">
        Your goal of <span className="font-semibold">{goals.calories}</span> kcal is a <span className={`font-semibold ${direction === "deficit" ? "text-emerald-400" : direction === "surplus" ? "text-amber-400" : "text-slate-300"}`}>{Math.abs(diff)} kcal/day {direction}</span>
        {direction !== "maintenance" && (
          <> — ≈ {Math.abs(lbPerWeek).toFixed(1)} lb/week {direction === "deficit" ? "loss" : "gain"}.</>
        )}
      </div>
    </section>
  );
}
